import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Star, Quote } from 'lucide-react';
import { Button } from '../ui/button';
import { publicApi } from '../../lib/api';
import { cn } from '../../lib/utils';
import { Skeleton } from '../ui/skeleton';

export function TestimonialsSlider() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState('next');

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      const response = await publicApi.getTestimonials();
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error loading testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextTestimonial = useCallback(() => {
    setDirection('next');
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  }, [testimonials.length]);

  const prevTestimonial = useCallback(() => {
    setDirection('prev');
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, [testimonials.length]);

  useEffect(() => {
    if (testimonials.length === 0) return;
    const interval = setInterval(nextTestimonial, 6000);
    return () => clearInterval(interval);
  }, [testimonials.length, nextTestimonial]);

  const visibleTestimonials = () => {
    if (testimonials.length === 0) return [];
    const items = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % testimonials.length;
      items.push({ ...testimonials[index], position: i });
    }
    return items;
  };

  return (
    <section className="py-20 bg-[#FDFBF7] overflow-hidden" data-testid="testimonials-slider">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#FF6B35] text-sm font-semibold tracking-wider uppercase mb-3">
            💬 Testimonials
          </span>
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1A1A] mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            What Our Travelers Say
          </h2>
          <p className="text-[#4A4A4A] max-w-2xl mx-auto text-lg">
            Real stories from real travelers who explored the world with us.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl p-6">
                <Skeleton className="h-4 w-24 mb-4" />
                <Skeleton className="h-20 w-full mb-4" />
                <div className="flex items-center gap-3">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div>
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : testimonials.length > 0 ? (
          <div className="relative">
            {/* Testimonials Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {visibleTestimonials().map((testimonial, idx) => (
                <div
                  key={`${testimonial.id}-${idx}`}
                  className={cn(
                    'bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100',
                    'transform transition-all duration-500',
                    idx === 1 ? 'md:scale-105 md:shadow-xl md:z-10' : 'md:opacity-80'
                  )}
                >
                  {/* Quote Icon */}
                  <Quote className="w-10 h-10 text-[#FF6B35]/20 mb-4" />

                  {/* Rating */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={cn(
                          'w-5 h-5',
                          i < testimonial.rating
                            ? 'fill-[#E9C46A] text-[#E9C46A]'
                            : 'text-gray-200'
                        )}
                      />
                    ))}
                  </div>

                  {/* Review */}
                  <p className="text-[#4A4A4A] mb-6 leading-relaxed line-clamp-4">
                    &ldquo;{testimonial.review}&rdquo;
                  </p>

                  {/* Customer Info */}
                  <div className="flex items-center gap-4">
                    {testimonial.photo ? (
                      <img
                        src={testimonial.photo}
                        alt={testimonial.customer_name}
                        className="w-14 h-14 rounded-full object-cover border-2 border-[#FF6B35]/20"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#E85A2D] flex items-center justify-center text-white text-xl font-semibold">
                        {testimonial.customer_name.charAt(0)}
                      </div>
                    )}
                    <div>
                      <h4 className="font-semibold text-[#1A1A1A]">
                        {testimonial.customer_name}
                      </h4>
                      <p className="text-sm text-[#4A4A4A]">{testimonial.location}</p>
                    </div>
                  </div>

                  {/* Package Name */}
                  {testimonial.package_name && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <span className="text-sm text-[#004E64] font-medium">
                        ✈️ {testimonial.package_name}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Navigation */}
            {testimonials.length > 3 && (
              <div className="flex justify-center gap-4 mt-10">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={prevTestimonial}
                  className="rounded-full w-12 h-12 border-[#004E64] text-[#004E64] hover:bg-[#004E64] hover:text-white"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={nextTestimonial}
                  className="rounded-full w-12 h-12 border-[#004E64] text-[#004E64] hover:bg-[#004E64] hover:text-white"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            )}

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-6">
              {testimonials.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={cn(
                    'h-2 rounded-full transition-all duration-300',
                    idx === currentIndex
                      ? 'w-6 bg-[#FF6B35]'
                      : 'w-2 bg-gray-300 hover:bg-gray-400'
                  )}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl">
            <p className="text-[#4A4A4A] text-lg">No testimonials available yet.</p>
          </div>
        )}
      </div>
    </section>
  );
}
